import { supabase } from "../supabase";
import { getActiveOrganizationId, subscribeActiveOrg } from "../org";
import { vendorStore } from "../vendors";
import type { ReviewMode } from "../org/types";
import type {
  Expense,
  ExpenseStatus,
  ExpenseStore,
  NewExpenseInput,
} from "./types";

const BUCKET = "receipts";

interface ExpenseRow {
  id: string;
  user_id: string;
  vendor: string;
  vendor_id: string | null;
  occurred_on: string;
  amount_cents: number;
  currency: string;
  category_id: string | null;
  tax_policy_id: string | null;
  tax_amount_cents: number;
  deductible_amount_cents: number | null;
  business_purpose: string | null;
  status: ExpenseStatus;
  employee: string | null;
  department: string | null;
  confidence_score: number | null;
  submitted_by: string | null;
  submitted_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

interface CategoryLookupRow { id: string; label: string; }
interface TaxPolicyLookupRow { id: string; category: string; }

const ROW_COLUMNS =
  "id, user_id, vendor, vendor_id, occurred_on, amount_cents, currency, category_id, tax_policy_id, tax_amount_cents, deductible_amount_cents, business_purpose, status, employee, department, confidence_score, submitted_by, submitted_at, approved_by, approved_at, rejection_reason, created_at, updated_at";

function extFromName(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot === -1 || dot === name.length - 1) return "bin";
  const cleaned = name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 8);
  return cleaned || "bin";
}

function rowToExpense(row: ExpenseRow, categoryLabel: string | null, taxPolicyLabel: string | null): Expense {
  return {
    id: row.id,
    vendor: row.vendor,
    vendorId: row.vendor_id,
    occurredOn: row.occurred_on,
    amountCents: Number(row.amount_cents),
    currency: row.currency,
    categoryId: row.category_id,
    categoryLabel,
    taxPolicyId: row.tax_policy_id,
    taxPolicyLabel,
    taxAmountCents: Number(row.tax_amount_cents ?? 0),
    deductibleAmountCents: row.deductible_amount_cents === null ? null : Number(row.deductible_amount_cents),
    businessPurpose: row.business_purpose,
    status: row.status,
    employee: row.employee,
    department: row.department,
    confidenceScore: row.confidence_score,
    createdBy: row.user_id,
    submittedBy: row.submitted_by,
    submittedAt: row.submitted_at,
    approvedBy: row.approved_by,
    approvedAt: row.approved_at,
    rejectionReason: row.rejection_reason,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function deriveStatus(hasFile: boolean): ExpenseStatus {
  return hasFile ? "ready" : "needs_info";
}

// ---------------------------------------------------------------------------
// Wave 17 — review mode enforcement helpers
// ---------------------------------------------------------------------------

interface ReviewContext {
  reviewMode: ReviewMode;
  isAdminOrOwner: boolean;
}

async function getReviewContext(orgId: string, userId: string): Promise<ReviewContext> {
  const [orgResult, memberResult] = await Promise.all([
    supabase.from("organizations").select("review_mode").eq("id", orgId).single(),
    supabase.from("org_members").select("role").eq("organization_id", orgId).eq("user_id", userId).eq("status", "active").single(),
  ]);
  const rawMode = (orgResult.data as { review_mode: string } | null)?.review_mode ?? "manual";
  const reviewMode: ReviewMode =
    rawMode === "auto_approve" || rawMode === "coming_soon" ? rawMode : "manual";
  const role = (memberResult.data as { role: string } | null)?.role ?? "employee";
  const isAdminOrOwner = role === "admin" || role === "owner";
  return { reviewMode, isAdminOrOwner };
}

/**
 * Resolve the initial status for a newly created record under the given
 * review context. Admin/owner uploads and auto_approve orgs skip the queue.
 * 'coming_soon' behaves like 'manual' (no auto-approve).
 */
function resolveInitialStatus(baseStatus: ExpenseStatus, ctx: ReviewContext): ExpenseStatus {
  if (ctx.isAdminOrOwner || ctx.reviewMode === "auto_approve") {
    // Only promote statuses that are in the "waiting for review" bucket.
    if (baseStatus === "draft" || baseStatus === "needs_info" || baseStatus === "ready") {
      return "approved";
    }
  }
  return baseStatus;
}

export function createSupabaseExpenseStore(): ExpenseStore {
  const listeners = new Set<() => void>();
  let snapshot: Expense[] = [];
  const categoryLabels = new Map<string, string>();
  const taxPolicyLabels = new Map<string, string>();
  let initialized = false;
  let loadPromise: Promise<void> | null = null;

  function emit(): void { for (const l of listeners) l(); }
  function commit(next: Expense[]): void { snapshot = next; emit(); }

  function labelsFor(row: ExpenseRow) {
    return {
      categoryLabel: row.category_id ? (categoryLabels.get(row.category_id) ?? null) : null,
      taxPolicyLabel: row.tax_policy_id ? (taxPolicyLabels.get(row.tax_policy_id) ?? null) : null,
    };
  }

  function applyUpdate(updated: Expense): void {
    const idx = snapshot.findIndex((e) => e.id === updated.id);
    if (idx !== -1) { const next = snapshot.slice(); next[idx] = updated; commit(next); }
  }

  async function loadLookups(orgId: string): Promise<void> {
    const [cat, tax] = await Promise.all([
      supabase.from("categories").select("id, label").eq("organization_id", orgId),
      supabase.from("tax_policies").select("id, category").eq("organization_id", orgId),
    ]);
    categoryLabels.clear();
    if (!cat.error && cat.data) {
      for (const row of cat.data as CategoryLookupRow[]) { categoryLabels.set(row.id, row.label); }
    }
    taxPolicyLabels.clear();
    if (!tax.error && tax.data) {
      for (const row of tax.data as TaxPolicyLookupRow[]) { taxPolicyLabels.set(row.id, row.category); }
    }
  }

  async function loadFromServer(): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) { categoryLabels.clear(); taxPolicyLabels.clear(); commit([]); return; }
    const orgId = getActiveOrganizationId();
    if (!orgId) { categoryLabels.clear(); taxPolicyLabels.clear(); commit([]); return; }
    await loadLookups(orgId);
    const { data: rows, error } = await supabase.from("expenses").select(ROW_COLUMNS).eq("organization_id", orgId).neq("status", "archived").order("occurred_on", { ascending: false }).order("created_at", { ascending: false });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[expenses] list failed: ${error.message}`);
      commit([]); return;
    }
    const typedRows = (rows ?? []) as ExpenseRow[];
    const next = typedRows.map((row) => { const { categoryLabel, taxPolicyLabel } = labelsFor(row); return rowToExpense(row, categoryLabel, taxPolicyLabel); });
    commit(next);
  }

  function ensureLoaded(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (!loadPromise) { loadPromise = loadFromServer().finally(() => { initialized = true; loadPromise = null; }); }
    return loadPromise;
  }

  let authSubAttached = false;
  function attachAuthListener(): void {
    if (authSubAttached) return;
    authSubAttached = true;
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") { initialized = false; categoryLabels.clear(); taxPolicyLabels.clear(); commit([]); return; }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") { initialized = false; void ensureLoaded(); }
    });
    subscribeActiveOrg(() => { initialized = false; categoryLabels.clear(); taxPolicyLabels.clear(); void ensureLoaded(); });
  }

  return {
    list(): Expense[] { if (!initialized && !loadPromise) { void ensureLoaded(); } return snapshot; },
    get(id: string): Expense | undefined { return snapshot.find((e) => e.id === id); },

    async add(input: NewExpenseInput): Promise<Expense> {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) { throw new Error("You must be signed in to create an expense."); }
      const userId = userData.user.id;
      const orgId = getActiveOrganizationId();
      if (!orgId) { throw new Error("No active organization selected. Reload the page and try again."); }
      const expenseId = crypto.randomUUID();
      const hasFile = Boolean(input.file);
      const status = deriveStatus(hasFile);
      // Wave 9 — auto-link vendor before insert. Failures are swallowed so the
      // expense still gets created; we just surface vendor_id=null.
      let vendorId: string | null = null;
      if (input.vendor && input.vendor.trim()) {
        try {
          const vendor = await vendorStore.upsertByName(input.vendor);
          vendorId = vendor.id;
        } catch {
          vendorId = null;
        }
      }
      const { data: inserted, error: insertError } = await supabase.from("expenses").insert({
        id: expenseId, organization_id: orgId, user_id: userId,
        vendor: input.vendor, vendor_id: vendorId,
        occurred_on: input.occurredOn, amount_cents: input.amountCents,
        currency: input.currency ?? "USD", category_id: input.categoryId ?? null, tax_policy_id: input.taxPolicyId ?? null,
        tax_amount_cents: 0, deductible_amount_cents: null, business_purpose: input.businessPurpose ?? null,
        status, employee: input.employee ?? null, department: input.department ?? null,
      }).select(ROW_COLUMNS).single();
      if (insertError || !inserted) { throw new Error(`Failed to create expense: ${insertError?.message ?? "unknown error"}`); }
      if (input.categoryId && !categoryLabels.has(input.categoryId) && input.categoryLabel) { categoryLabels.set(input.categoryId, input.categoryLabel); }
      if (input.taxPolicyId && !taxPolicyLabels.has(input.taxPolicyId) && input.taxPolicyLabel) { taxPolicyLabels.set(input.taxPolicyId, input.taxPolicyLabel); }
      let row = inserted as ExpenseRow;

      // Wave 17 — enforce review mode: auto-approve if org setting says so,
      // or if the creator is admin/owner (self-approval for powerful roles).
      try {
        const ctx = await getReviewContext(orgId, userId);
        const resolved = resolveInitialStatus(row.status as ExpenseStatus, ctx);
        if (resolved !== row.status) {
          const nowIso = new Date().toISOString();
          const { data: updatedRow } = await supabase
            .from("expenses")
            .update({
              status: resolved,
              submitted_by: userId,
              submitted_at: nowIso,
              approved_by: userId,
              approved_at: nowIso,
            })
            .eq("id", expenseId)
            .select(ROW_COLUMNS)
            .single();
          if (updatedRow) row = updatedRow as ExpenseRow;
        }
      } catch {
        // Non-fatal: fall back to the original status if review context lookup fails.
      }

      const { categoryLabel, taxPolicyLabel } = labelsFor(row);
      const expense = rowToExpense(row, categoryLabel, taxPolicyLabel);
      initialized = true;
      commit([expense, ...snapshot]);
      if (input.file) {
        const ext = extFromName(input.file.name);
        const receiptId = crypto.randomUUID();
        const storagePath = `${userId}/${receiptId}.${ext}`;
        const { error: uploadError } = await supabase.storage.from(BUCKET).upload(storagePath, input.file, { contentType: input.file.type || "application/octet-stream", upsert: false });
        if (uploadError) {
          const idx = snapshot.findIndex((e) => e.id === expenseId);
          if (idx !== -1) { const updated: Expense = { ...snapshot[idx]!, status: "needs_info" }; const next = snapshot.slice(); next[idx] = updated; commit(next); }
          throw new Error(`Receipt upload failed: ${uploadError.message}`);
        }
        const { error: receiptInsertError } = await supabase.from("receipts").insert({
          id: receiptId, organization_id: orgId, user_id: userId, filename: input.file.name,
          mime_type: input.file.type || "application/octet-stream", size_bytes: input.file.size,
          storage_path: storagePath, linked_expense_id: expenseId, linked_invoice_id: null,
        });
        if (receiptInsertError) {
          await supabase.storage.from(BUCKET).remove([storagePath]);
          throw new Error(`Failed to record receipt: ${receiptInsertError.message}`);
        }
      }
      return expense;
    },

    async addMany(inputs: NewExpenseInput[]): Promise<Expense[]> {
      if (inputs.length === 0) return [];

      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("You must be signed in to import expenses.");
      }
      const userId = userData.user.id;

      const orgId = getActiveOrganizationId();
      if (!orgId) {
        throw new Error(
          "No active organization selected. Reload the page and try again.",
        );
      }

      // Wave 9 — resolve vendor ids serially up front. Same rationale as the
      // localStorage addMany: lets repeat vendors short-circuit against the
      // upsert cache without N parallel race-inserts.
      const vendorIds: Array<string | null> = [];
      for (const input of inputs) {
        if (!input.vendor || !input.vendor.trim()) {
          vendorIds.push(null);
          continue;
        }
        try {
          const vendor = await vendorStore.upsertByName(input.vendor);
          vendorIds.push(vendor.id);
        } catch {
          vendorIds.push(null);
        }
      }

      // CSV import doesn't attach receipts, so every row is `needs_info`
      // unless the caller somehow passed a file (which the importer won't).
      const rowsToInsert = inputs.map((input, idx) => ({
        id: crypto.randomUUID(),
        organization_id: orgId,
        user_id: userId,
        vendor: input.vendor,
        vendor_id: vendorIds[idx] ?? null,
        occurred_on: input.occurredOn,
        amount_cents: input.amountCents,
        currency: input.currency ?? "USD",
        category_id: input.categoryId ?? null,
        tax_policy_id: input.taxPolicyId ?? null,
        tax_amount_cents: 0,
        deductible_amount_cents: null,
        business_purpose: input.businessPurpose ?? null,
        status: deriveStatus(Boolean(input.file)),
        employee: input.employee ?? null,
        department: input.department ?? null,
      }));

      const { data: inserted, error: insertError } = await supabase
        .from("expenses")
        .insert(rowsToInsert)
        .select(ROW_COLUMNS);

      if (insertError || !inserted) {
        throw new Error(
          `Failed to import expenses: ${insertError?.message ?? "unknown error"}`,
        );
      }

      // Backfill any new category/tax policy labels from the inputs so the
      // UI can render labels for the freshly-imported rows without a refetch.
      for (const input of inputs) {
        if (
          input.categoryId &&
          input.categoryLabel &&
          !categoryLabels.has(input.categoryId)
        ) {
          categoryLabels.set(input.categoryId, input.categoryLabel);
        }
        if (
          input.taxPolicyId &&
          input.taxPolicyLabel &&
          !taxPolicyLabels.has(input.taxPolicyId)
        ) {
          taxPolicyLabels.set(input.taxPolicyId, input.taxPolicyLabel);
        }
      }

      let typedRows = inserted as ExpenseRow[];

      // Wave 17 — enforce review mode for bulk imports.
      try {
        const ctx = await getReviewContext(orgId, userId);
        const idsToApprove = typedRows
          .filter((r) => {
            const resolved = resolveInitialStatus(r.status as ExpenseStatus, ctx);
            return resolved !== r.status;
          })
          .map((r) => r.id);
        if (idsToApprove.length > 0) {
          const nowIso = new Date().toISOString();
          const { data: approvedRows } = await supabase
            .from("expenses")
            .update({
              status: "approved",
              submitted_by: userId,
              submitted_at: nowIso,
              approved_by: userId,
              approved_at: nowIso,
            })
            .in("id", idsToApprove)
            .select(ROW_COLUMNS);
          if (approvedRows) {
            const updatedMap = new Map((approvedRows as ExpenseRow[]).map((r) => [r.id, r]));
            typedRows = typedRows.map((r) => updatedMap.get(r.id) ?? r);
          }
        }
      } catch {
        // Non-fatal: keep original statuses on failure.
      }

      const newExpenses = typedRows.map((row) => {
        const categoryLabel = row.category_id
          ? (categoryLabels.get(row.category_id) ?? null)
          : null;
        const taxPolicyLabel = row.tax_policy_id
          ? (taxPolicyLabels.get(row.tax_policy_id) ?? null)
          : null;
        return rowToExpense(row, categoryLabel, taxPolicyLabel);
      });

      initialized = true;
      // Prepend so the newest imports appear at the top, matching `add()`.
      commit([...newExpenses, ...snapshot]);

      return newExpenses;
    },

    async update(id: string, patch: Partial<Omit<Expense, "id" | "createdAt" | "updatedAt">>): Promise<Expense> {
      const dbPatch: Record<string, unknown> = {};
      if (patch.vendor !== undefined) dbPatch.vendor = patch.vendor;
      if (patch.occurredOn !== undefined) dbPatch.occurred_on = patch.occurredOn;
      if (patch.amountCents !== undefined) dbPatch.amount_cents = patch.amountCents;
      if (patch.currency !== undefined) dbPatch.currency = patch.currency;
      if (patch.categoryId !== undefined) dbPatch.category_id = patch.categoryId;
      if (patch.taxPolicyId !== undefined) dbPatch.tax_policy_id = patch.taxPolicyId;
      if (patch.taxAmountCents !== undefined) dbPatch.tax_amount_cents = patch.taxAmountCents;
      if (patch.deductibleAmountCents !== undefined) dbPatch.deductible_amount_cents = patch.deductibleAmountCents;
      if (patch.businessPurpose !== undefined) dbPatch.business_purpose = patch.businessPurpose;
      if (patch.status !== undefined) dbPatch.status = patch.status;
      if (patch.employee !== undefined) dbPatch.employee = patch.employee;
      if (patch.department !== undefined) dbPatch.department = patch.department;
      const { data, error } = await supabase.from("expenses").update(dbPatch).eq("id", id).select(ROW_COLUMNS).single();
      if (error || !data) { throw new Error(`Failed to update expense: ${error?.message ?? "unknown"}`); }
      const row = data as ExpenseRow;
      if (patch.categoryLabel && row.category_id) { categoryLabels.set(row.category_id, patch.categoryLabel); }
      if (patch.taxPolicyLabel && row.tax_policy_id) { taxPolicyLabels.set(row.tax_policy_id, patch.taxPolicyLabel); }
      const { categoryLabel, taxPolicyLabel } = labelsFor(row);
      const updated = rowToExpense(row, categoryLabel, taxPolicyLabel);
      applyUpdate(updated);
      return updated;
    },

    async setStatus(id: string, status: ExpenseStatus): Promise<Expense> {
      const { data, error } = await supabase.from("expenses").update({ status }).eq("id", id).select(ROW_COLUMNS).single();
      if (error || !data) { throw new Error(`Failed to update status: ${error?.message ?? "unknown"}`); }
      const row = data as ExpenseRow;
      const { categoryLabel, taxPolicyLabel } = labelsFor(row);
      const updated = rowToExpense(row, categoryLabel, taxPolicyLabel);
      applyUpdate(updated);
      return updated;
    },

    async remove(id: string): Promise<void> {
      const target = snapshot.find((e) => e.id === id);
      if (!target) return;
      const next = snapshot.filter((e) => e.id !== id);
      commit(next);
      void supabase.from("expenses").delete().eq("id", id).then(({ error }) => {
        if (error) {
          // eslint-disable-next-line no-console
          console.warn(`[expenses] delete failed: ${error.message}`);
        }
      });
    },

    async submit(id: string): Promise<Expense> {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) { throw new Error("You must be signed in to submit an expense."); }
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase.from("expenses").update({
        status: "pending", submitted_by: userData.user.id, submitted_at: nowIso,
      }).eq("id", id).select(ROW_COLUMNS).single();
      if (error || !data) { throw new Error(`Failed to submit expense: ${error?.message ?? "unknown"}`); }
      const row = data as ExpenseRow;
      const { categoryLabel, taxPolicyLabel } = labelsFor(row);
      const updated = rowToExpense(row, categoryLabel, taxPolicyLabel);
      applyUpdate(updated);
      return updated;
    },

    async approve(id: string): Promise<Expense> {
      const { data, error } = await supabase.rpc("approve_expense", { expense_id: id, action: "approve", reason: null });
      if (error) { throw new Error(`Approve failed: ${error.message}`); }
      const row = data as ExpenseRow;
      const { categoryLabel, taxPolicyLabel } = labelsFor(row);
      const updated = rowToExpense(row, categoryLabel, taxPolicyLabel);
      applyUpdate(updated);
      return updated;
    },

    async reject(id: string, reason: string): Promise<Expense> {
      const { data, error } = await supabase.rpc("approve_expense", { expense_id: id, action: "reject", reason });
      if (error) { throw new Error(`Reject failed: ${error.message}`); }
      const row = data as ExpenseRow;
      const { categoryLabel, taxPolicyLabel } = labelsFor(row);
      const updated = rowToExpense(row, categoryLabel, taxPolicyLabel);
      applyUpdate(updated);
      return updated;
    },

    subscribe(listener: () => void): () => void {
      attachAuthListener();
      if (!initialized && !loadPromise) { void ensureLoaded(); }
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
}
